import React, {useState, useEffect, useMemo} from 'react';
import {Stack, Grid, Box, Divider, Typography} from '@mui/material';
import '../FieldTypes.scss';
import './AssignmentFieldType.scss';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../../shared/interfaces';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import {
  useFieldData,
  useUserList,
} from '../../../../../../hooks/session-search/useSessionSearch';
import {IWorkProductItemsDetail} from '../../../../../../api/types/sessionSearch.type';
import {FieldTypeProps} from '../FieldTypeProps';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../../shared/constants';
import {t} from 'i18next';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrCheckboxList from '../../../../../common/arr-checkbox-list/ArrCheckboxList';
import {ISelectItem} from '../../../../../../shared/types';
import ArrDatePicker from '../../../../../common/arr-date-picker/ArrDatePicker';

export interface AssignmentFieldTypeProps extends Omit<
  FieldTypeProps,
  'onCriteriaChaged'
> {
  onCriteriaChaged?: (value: string) => void;
}

const statusItems: ISelectItem[] = [
  {
    value: '0',
    label: t('sessionSearch.queryList.workProduct.assignmentsDrawer.choose'),
  },
  {
    value: 'completed',
    label: t('sessionSearch.queryList.workProduct.assignmentsDrawer.completed'),
  },
  {
    value: 'assigned',
    label: t('sessionSearch.queryList.workProduct.assignmentsDrawer.assigned'),
  },
];

const AssignmentFieldType: React.FC<AssignmentFieldTypeProps> = ({
  onCriteriaChaged,
  onValidated,
}) => {
  const [showAssignmentsFieldListToast, setShowAssignmentsFieldListToast] =
    useState(true);
  const [showAssignmentsUserListToast, setShowAssignmentsUserListToast] =
    useState(true);
  const [showDaterange, setShowDaterange] = useState(false);

  const [selectedDistributionList, setselectedDistributionList] = useState<
    string[]
  >([]);

  const [selectedAssignmentFields, setSelectedAssignmentFields] = useState<
    string[] | null
  >([]);
  const [selectedAssignmentFieldNames, setSelectedAssignmentFieldNames] =
    useState<string[] | null>([]);
  const [selectItems, setSelectItems] = useState<ISelectItem[]>(statusItems);
  const [selectedStatus, setSelectedStatus] = useState<ISelectItem | null>(
    statusItems[0]
  );
  const [startDateValue, setStartDateValue] = useState<string | null>(null);
  const [endDateValue, setEndDateValue] = useState<string | null>(null);
  const {
    data: assignmentData,
    isError: errorGetAssignments,
    isPending: isLoadingAssignments,
  } = useFieldData('ASSIGNMENTS');
  const {
    data: userList,
    isError: errorLoadingUsers,
    isPending: isLoadingUserList,
  } = useUserList();

  useEffect(() => {
    setSelectItems(statusItems);
  }, []);
  useEffect(() => {
    if (
      selectedStatus?.value == 'completed' ||
      selectedStatus?.value == 'assigned'
    ) {
      setShowDaterange(true);
    } else {
      setShowDaterange(false);
    }
  }, [selectedStatus]);

  const convertToTree = (
    source: IWorkProductItemsDetail[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    // Build a lookup map of id → node
    const map: Record<string, TreeViewBaseItem<ExtendedTreeItemProps>> = {};

    // First pass: create all nodes
    source.forEach((item) => {
      map[item.id] = {
        id: item.id,
        label: item.text ?? '',
        name: item.text,
        fileType: item.isGroup ? 'folder' : 'file',
        parentFolder: item.parent !== '#' ? item.parent : undefined,
        parentTagName: item.parent !== '#' ? item.parent : undefined,
        type: item.type,
        children: item.isGroup ? [] : [], // always defined
      };
    });

    const roots: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    // Second pass: assign children to parents
    source.forEach((item) => {
      if (item.parent && item.parent !== '#' && map[item.parent]) {
        const parentNode = map[item.parent];
        parentNode.children = parentNode.children || [];
        parentNode.children.push(map[item.id]);
      } else {
        // root node
        roots.push(map[item.id]);
      }
    });

    return roots;
  };

  const userListCheckboxItems = useMemo(() => {
    if (!userList || errorLoadingUsers || isLoadingUserList) return [];
    return userList.map((user) => ({
      label: user.value,
      value: false,
    }));
  }, [userList, errorLoadingUsers, isLoadingUserList]);

  const treeData = useMemo(() => {
    if (!assignmentData?.workProductItemsDetails?.length) return [];
    return convertToTree(assignmentData.workProductItemsDetails);
  }, [assignmentData]);

  const handleSelected = (values: string[] | null) => {
    if (values) {
      const selectedFields: string[] = [];
      values.forEach((value) => {
        const selected = assignmentData?.workProductItemsDetails.find(
          (x) => x.id == value && x.isGroup == false
        );
        if (selected) {
          selectedFields.push(selected.text ? `"${selected.text}"` : '');
        }
      });
      setSelectedAssignmentFieldNames(selectedFields);
      setSelectedAssignmentFields(values);
    }
  };

  const renderDateRange = () => {
    let component = <></>;
    if (showDaterange) {
      component = (
        <>
          <Box>
            <Typography
              className="typo-text subhead4_size right-pane_header"
              data-testid={`assignment-field-type-date-range`}
              tabIndex={0}
            >
              {t(
                'sessionSearch.queryList.workProduct.assignmentsDrawer.dateRange'
              )}
            </Typography>
          </Box>
          <Box className="add-on-pane">
            <ArrDatePicker
              type="date-picker"
              label={t(
                'sessionSearch.queryList.workProduct.assignmentsDrawer.startDate'
              )}
              onChange={(value) => setStartDateValue(value as string)}
              applyEnable={true}
              dataTestId="date-field-start"
            />
          </Box>
          <Box className="add-on-pane">
            <ArrDatePicker
              type="date-picker"
              label={t(
                'sessionSearch.queryList.workProduct.assignmentsDrawer.endDate'
              )}
              onChange={(value) => setEndDateValue(value as string)}
              applyEnable={true}
              dataTestId="date-field-end"
            />
          </Box>
        </>
      );
    }
    return component;
  };

  useEffect(() => {
    const hasDistribution = selectedDistributionList?.length > 0;
    const hasStatus =
      selectedStatus?.value == 'completed' ||
      selectedStatus?.value == 'assigned';
    const hasAssignments =
      selectedAssignmentFieldNames && selectedAssignmentFieldNames?.length > 0;

    let isValid = hasDistribution || hasStatus || hasAssignments;

    if (isValid) {
      if (startDateValue && endDateValue) {
        isValid = isValidDateRange(startDateValue, endDateValue);
      }
    }

    if (!isValid) {
      onValidated?.(false);
      return;
    }

    onValidated?.(true);

    const parts: string[] = [];

    if (hasAssignments) {
      parts.push(` name: [ ${selectedAssignmentFieldNames.join(', ')} ]`);
    }

    if (hasDistribution) {
      parts.push(` distributedTo: [ ${selectedDistributionList.join(', ')} ]`);
    }

    if (hasStatus) {
      parts.push(` ${selectedStatus!.value}: "true"`);

      const dateParts: string[] = [];
      if (startDateValue) {
        dateParts.push(`"from" : "${startDateValue}"`);
      }
      if (endDateValue) {
        dateParts.push(`"to" : "${endDateValue}"`);
      }

      const dateLabel =
        selectedStatus!.value === 'completed'
          ? ' completeddate'
          : ' assigneddate';

      if (startDateValue || endDateValue) {
        parts.push(`${dateLabel}: [ ${dateParts.join(', ')} ]`);
      }
    }

    const assignment = `assignments: [${parts.join(', ')} ]`;

    onCriteriaChaged && onCriteriaChaged(assignment.trim());
  }, [
    selectedDistributionList,
    selectedStatus,
    selectedAssignmentFieldNames,
    startDateValue,
    endDateValue,
  ]);

  const isValidDateRange = (
    startDateValue: string,
    endDateValue: string
  ): boolean => {
    // Parse the strings into Date objects
    const startDate = new Date(startDateValue);
    const endDate = new Date(endDateValue);

    // Check for invalid dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return false;
    }

    // Return false if end date is before start date
    return endDate >= startDate;
  };

  const handleCheckboxListChange = (checked: boolean[]) => {
    if (userList) {
      const selectedUserIds: string[] = [];
      for (let i = 0; i < checked.length; i++) {
        if (checked[i] === true) {
          selectedUserIds.push(userList[i].key.toString());
        }
      }

      setselectedDistributionList(selectedUserIds);
    }
  };

  return (
    <>
      <Grid container className="assignments-panel">
        <Grid size={6} className="assignments-panel_left-pane">
          <Stack spacing={2}>
            <Box className="left-pane_header_box">
              <Typography
                className="typo-text subhead4_size left-pane_header"
                data-testid={`assignment-field-type-select-assignments`}
                tabIndex={0}
              >
                {t(
                  'sessionSearch.queryList.workProduct.assignmentsDrawer.selectAssignments'
                )}
              </Typography>
            </Box>
            <Box className="left-pane-divider">
              <Divider className="assignments-divider" />
            </Box>
            <Box className="assignments-tree-list" tabIndex={0}>
              <ArrTreeView
                itemsList={treeData}
                selectedItem={''}
                allowGlobalSelect={false}
                treeSelectedItems={selectedAssignmentFields}
                multipleSelection={true}
                isShowCountLabel={true}
                isShowCheckbox={true}
                showActions={true}
                customContentHeight={56}
                setSelectedItem={handleSelected}
              />
            </Box>
            <Box>
              <ArrLoadingSpinner show={isLoadingAssignments} />
            </Box>
          </Stack>
        </Grid>

        {/* Right Panel */}
        <Grid size={6} className="assignments-panel_right-pane">
          <Stack spacing={2}>
            <Box>
              <Typography
                className="typo-text subhead4_size right-pane_header"
                data-testid={`assignment-field-type-distributed-to-label`}
                tabIndex={0}
              >
                {t(
                  'sessionSearch.queryList.workProduct.assignmentsDrawer.distributedToLabel'
                )}
              </Typography>
            </Box>
            <Box>
              <Divider className="assignments-divider" />
            </Box>
            <Box className="distribution-checkbox-list" tabIndex={0}>
              <ArrCheckboxList
                childLabels={userListCheckboxItems}
                onChange={handleCheckboxListChange}
                showSelectAll={false}
                size="small"
                tabIndex={-1}
              />
            </Box>
            <Box className="add-on-pane">
              <ArrSingleSelectDropdown
                dropdownLabel={t(
                  'sessionSearch.queryList.workProduct.assignmentsDrawer.selectStatus'
                )}
                dropdownItemKey="value"
                dropdownItemDisplay="label"
                dropdownItems={selectItems}
                selectedValues={selectedStatus}
                onSelectionChange={setSelectedStatus}
                sizeVariant="large"
                dataTestId="asd-select-assignment-status-field"
              />
            </Box>
            {renderDateRange()}
            <Box>
              <ArrLoadingSpinner show={isLoadingUserList} />
            </Box>
          </Stack>
        </Grid>

        {errorGetAssignments && showAssignmentsFieldListToast && (
          <ArrToastNotification
            message={t(
              'sessionSearch.queryList.workProduct.assignmentsDrawer.assignmentFieldsError'
            )}
            status={[ToastType.Error]}
            onClose={() => setShowAssignmentsFieldListToast(false)}
          />
        )}

        {errorLoadingUsers && showAssignmentsUserListToast && (
          <ArrToastNotification
            message={t(
              'sessionSearch.queryList.workProduct.assignmentsDrawer.assignmentUserListError'
            )}
            status={[ToastType.Error]}
            onClose={() => setShowAssignmentsUserListToast(false)}
          />
        )}
      </Grid>
    </>
  );
};
export default AssignmentFieldType;
